import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { login } from '../api';
import { useAuthStore } from '../store/auth';
import { productTokens } from '../theme';

interface LocationState {
  from?: { pathname?: string };
}

function IrisBadge() {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        backgroundColor: alpha('#ffffff', 0.1),
        border: `1px solid ${alpha('#ffffff', 0.18)}`,
      }}
    >
      <svg
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.75 12C3.78 8.51 7.47 6 12 6C16.53 6 20.22 8.51 22.25 12C20.22 15.49 16.53 18 12 18C7.47 18 3.78 15.49 1.75 12Z"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
      </svg>
    </Box>
  );
}

function UserGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5.5 19C6.7 15.9 8.96 14.5 12 14.5C15.04 14.5 17.3 15.9 18.5 19"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LockGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="5.25"
        y="10.25"
        width="13.5"
        height="9.5"
        rx="2.2"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M8.5 10V7.75C8.5 5.68 10.18 4 12.25 4C14.32 4 16 5.68 16 7.75V10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

const fieldSx = {
  '& .MuiOutlinedInput-input': {
    py: 1.45,
    fontSize: '0.98rem',
  },
};

function LeftPanel() {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 280, md: '100dvh' },
        overflow: 'hidden',
        color: '#f5fbf8',
        backgroundColor: productTokens.iris,
        backgroundImage: `linear-gradient(180deg, ${alpha('#ffffff', 0.08)} 0%, ${alpha('#071923', 0.2)} 100%)`,
        borderRight: { md: `1px solid ${alpha('#ffffff', 0.14)}` },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IrisBadge />
          <Box>
            <Typography sx={{ fontSize: '1.28rem', fontWeight: 800, lineHeight: 1.1 }}>
              Iris
            </Typography>
            <Typography
              sx={{ color: alpha('#f5fbf8', 0.72), fontSize: '0.82rem', fontWeight: 650 }}
            >
              儿童交互评估
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.5} sx={{ maxWidth: 430, pb: { xs: 0, md: 2 } }}>
          <Typography
            sx={{
              fontSize: { xs: '2.15rem', sm: '2.6rem', md: '3rem' },
              lineHeight: 1.08,
              fontWeight: 780,
            }}
          >
            继续评估流程，查看儿童交互记录。
          </Typography>
          <Typography
            sx={{
              maxWidth: 360,
              color: alpha('#f5fbf8', 0.76),
              fontSize: '1rem',
              lineHeight: 1.75,
            }}
          >
            面向儿童发育研究的眼动采集工作台。
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation<LocationState>();
  const { setAuthenticated } = useAuthStore();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { username: resolvedUsername } = await login(username, password);
      setAuthenticated(resolvedUsername);
      const dest = location.state?.from?.pathname || '/dashboard';
      navigate(dest, { replace: true });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '登录失败';
      if (/登录失败 \(401\)/.test(message)) {
        navigate('/auth/status?flow=login&state=failed', {
          replace: true,
          state: { message: '请确认账号已完成激活，或检查用户名与密码是否正确。' },
        });
        return;
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        px: 0,
        flex: 1,
        display: 'flex',
        minHeight: '100dvh',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(360px, 0.95fr) minmax(420px, 1fr)' },
          minHeight: { xs: 'auto', md: '100dvh' },
        }}
      >
        <LeftPanel />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { md: '100dvh' },
            px: { xs: 3, sm: 4, md: 6 },
            py: { xs: 4, sm: 5, md: 6 },
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 460 }}>
            <Stack spacing={3.25}>
              <Box>
                <Typography variant="h4" sx={{ color: 'secondary.dark', mb: 1 }}>
                  欢迎回来
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.98rem', lineHeight: 1.7 }}>
                  需要访问？{' '}
                  <Link
                    component={RouterLink}
                    to="/register"
                    underline="hover"
                    sx={{ fontWeight: 800, color: 'secondary.dark' }}
                  >
                    申请账号
                  </Link>
                </Typography>
              </Box>

              <Box component="form" onSubmit={onSubmit} noValidate>
                <Stack spacing={2.25}>
                  <TextField
                    label="用户名"
                    variant="outlined"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="请输入研究员账号"
                    inputProps={{ 'aria-label': '用户名' }}
                    required
                    autoFocus
                    autoComplete="username"
                    fullWidth
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ color: alpha(productTokens.iris, 0.7), mr: 1 }}
                        >
                          <UserGlyph />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="密码"
                    variant="outlined"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="请输入登录密码"
                    inputProps={{ 'aria-label': '密码' }}
                    required
                    autoComplete="current-password"
                    fullWidth
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ color: alpha(productTokens.iris, 0.7), mr: 1 }}
                        >
                          <LockGlyph />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error ? <Alert severity="error">{error}</Alert> : null}

                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={busy}
                    fullWidth
                    size="large"
                    sx={{ mt: 0.5 }}
                  >
                    {busy ? (
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <CircularProgress size={18} color="inherit" />
                        <span>正在验证</span>
                      </Stack>
                    ) : (
                      '进入工作台'
                    )}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
